import { UIEvent, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Path } from '#/service/routes';
import { techSearchParams } from '#/service/utils';

const debugLog = (_debugInfo?: string, ..._params: any[]) => {
  // uncomment the console.log here to get debug info about all the redirections.
  // console.log('useNavigation:', _debugInfo, ..._params);
};

export interface UseNavigationResult {
  /**
   * Navigates to the previous page in the history.
   * If there is not at least 3 entries in the history it navigates to the home page instead.
   * Unless there is a different fallback path is provided.
   * @param [fallbackPathOrEvent] {Path | UIEvent} Where to navigate to if we can't go back.
   * The UIEvent type is only allowed to make the function directly usable as an event handler.
   */
  goBack: (fallbackPathOrEvent?: Path | UIEvent) => void;
  goHome: () => void;
  /**
   * Method used for navigating between route states
   * @param path {string} The path to navigate to.
   * @param state {Object} The state to pass to the new route.
   * Please do not put any sensitive data in here.
   * Also do not put too much data here as it may affect performance,
   * because state is often serialized and deserialized
   */
  go: (path: string, state?: Object) => void;
  reloadPage: () => void;
}

/**
 * Common navigation actions.
 * @param debugInfo {string} Info about what component is using navigation and why. For debugging.
 * @returns {UseNavigationResult} Common navigation functions.
 */
export const useNavigation = (debugInfo: string): UseNavigationResult => {
  const navigate = useNavigate();
  const location = useLocation();

  // to prevent unnecessary re-renders, we don't want our functions to depend on the location. using refs instead.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = useRef(location);
  locationRef.current = location;

  const goHome = useCallback(() => {
    debugLog(debugInfo, 'goHome');

    // if already at home, just scroll to top
    if (locationRef.current.pathname === '/') {
      window.scrollTo(0, 0);
    } else {
      navigateRef.current({
        pathname: `/`,
        search: techSearchParams(locationRef.current.search),
      });
    }
  }, [debugInfo]);

  const goBack = useCallback(
    (fallbackPath?: Path | UIEvent) => {
      if (window.history.length >= 3) {
        // if the page was deep linked, go back to the home page
        // note: first entry is *almost* always the new tab/window, hence the 3 instead of 2 here.
        debugLog(debugInfo, 'goBack');
        navigateRef.current(-1);
      } else if (typeof fallbackPath === 'string') {
        // we have to make sure that fallbackPath is a string, because when goBack is used directly as an event
        // handler (like <button onClick={goBack} />), the first parameter will be a UIEvent object.
        debugLog(debugInfo, 'goBack→fallbackPath', fallbackPath);
        navigateRef.current(
          {
            pathname: fallbackPath,
            search: techSearchParams(locationRef.current.search),
          },
          { replace: true }
        );
      } else {
        debugLog(debugInfo, 'goBack→goHome');
        goHome();
      }
    },
    [debugInfo, goHome]
  );

  const go = useCallback(
    (path: string, state?: Object) => {
      debugLog(debugInfo, 'go');
      navigateRef.current(
        {
          pathname: path,
          search: techSearchParams(locationRef.current.search),
        },
        {
          state,
        }
      );
    },
    [debugInfo]
  );

  const reloadPage = useCallback(() => {
    debugLog(debugInfo, 'reloadPage');
    navigateRef.current(0);
  }, [debugInfo]);

  return {
    goHome,
    goBack,
    go,
    reloadPage,
  };
};
